import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ROLE_LABELS } from "@/lib/format";
import { Plus, PencilSimple, Trash, Gear, Eye, EyeSlash } from "@phosphor-icons/react";

const ROLE_KEYS = ["admin", "manager", "master", "storekeeper", "accountant"];
const empty = { email: "", password: "", password2: "", name: "", role: "manager", active: true };

export default function Users() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  const [rolesOpen, setRolesOpen] = useState(false);
  const [permCfg, setPermCfg] = useState(null); // { roles, modules, permissions }

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openRoles = async () => {
    try { const { data } = await api.get("/role-permissions"); setPermCfg(data); setRolesOpen(true); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const togglePerm = (role, mod) => {
    setPermCfg((p) => {
      const cur = new Set(p.permissions[role] || []);
      cur.has(mod) ? cur.delete(mod) : cur.add(mod);
      return { ...p, permissions: { ...p.permissions, [role]: Array.from(cur) } };
    });
  };
  const savePerms = async () => {
    try { await api.put("/role-permissions", { permissions: permCfg.permissions }); toast.success("Права ролей збережено"); setRolesOpen(false); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Заповніть обовʼязкові поля");
    if (!editId && !form.password) return toast.error("Введіть пароль");
    if (form.password && form.password.length < 6) return toast.error("Пароль має містити щонайменше 6 символів");
    if (form.password && form.password !== form.password2) return toast.error("Паролі не співпадають");
    try {
      if (editId) await api.put(`/users/${editId}`, { email: form.email, name: form.name, role: form.role, active: form.active, password: form.password });
      else await api.post("/users", { email: form.email, password: form.password, name: form.name, role: form.role });
      toast.success("Збережено"); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const edit = (u) => { setForm({ email: u.email, password: "", password2: "", name: u.name, role: u.role, active: u.active }); setEditId(u.id); setShowPwd(false); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Видалити користувача?")) return; try { await api.delete(`/users/${id}`); toast.success("Видалено"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Персонал і ролі" subtitle={`Користувачів: ${list.length}`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openRoles} data-testid="role-settings-btn"><Gear size={18} className="mr-1" /> Налаштування ролей</Button>
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
              <DialogTrigger asChild><Button data-testid="add-user-btn"><Plus size={18} className="mr-1" /> Користувач</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editId ? "Редагувати користувача" : "Новий користувач"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Імʼя *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="user-email" /></div>
                  <div><Label>{editId ? "Новий пароль (не обовʼязково)" : "Пароль *"}</Label>
                    <div className="relative mt-1.5">
                      <Input type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pr-10" data-testid="user-password" />
                      <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="toggle-password" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {form.password && form.password.length < 6 && <p className="text-xs text-destructive mt-1.5" data-testid="password-hint">Мінімум 6 символів</p>}
                  </div>
                  <div><Label>Повторіть пароль</Label>
                    <Input type={showPwd ? "text" : "password"} value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} className="mt-1.5" data-testid="user-password2" />
                    {form.password2 && form.password !== form.password2 && <p className="text-xs text-destructive mt-1.5" data-testid="password-match-hint">Паролі не співпадають</p>}
                  </div>
                  <div><Label>Роль</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLE_KEYS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {editId && <div className="flex items-center justify-between"><Label>Активний</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active" /></div>}
                </div>
                <DialogFooter><Button onClick={save} data-testid="save-user-btn">Зберегти</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Dialog open={rolesOpen} onOpenChange={setRolesOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Налаштування ролей — доступ до розділів</DialogTitle></DialogHeader>
          {permCfg && (
            <div className="overflow-x-auto">
              <p className="text-sm text-muted-foreground mb-4">Оберіть, які розділи сайту може переглядати кожна роль. Адміністратор завжди має повний доступ.</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-semibold">Розділ</th>
                    {permCfg.roles.map((r) => <th key={r.key} className="px-2 py-2 font-semibold text-center whitespace-nowrap">{r.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {permCfg.modules.map((m) => (
                    <tr key={m.key} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 font-medium">{m.label}</td>
                      {permCfg.roles.map((r) => (
                        <td key={r.key} className="px-2 py-2 text-center">
                          <Switch
                            checked={(permCfg.permissions[r.key] || []).includes(m.key)}
                            onCheckedChange={() => togglePerm(r.key, m.key)}
                            data-testid={`perm-${r.key}-${m.key}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button onClick={savePerms} data-testid="save-roles-btn">Зберегти права</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Імʼя</TableHead><TableHead>Email</TableHead><TableHead>Роль</TableHead>
            <TableHead>Статус</TableHead><TableHead className="text-right">Дії</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>{u.role_label}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-green-500/20 text-green-500" : "bg-secondary text-muted-foreground"}`}>
                    {u.active ? "Активний" : "Вимкнено"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => edit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={18} /></button>
                  <button className="text-muted-foreground hover:text-destructive p-1" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash size={18} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
